<template>
    <div class="videoWrapper" :class="fadeClass" :style="{ transition: fadeTime + 'ms linear opacity' }">
        <video ref="videoPlayer" class="video-js vjs-default-skin vjs-16-9"></video>
    </div>
</template>

<script>
    import videojs from 'video.js';
    import FadeMixin from '../mixins/FadeMixin';
    import SetDurationMixin from '../mixins/SetDurationMixin';

    const goalBufferLength = 15;
    const maxGoalBufferLength = 30;

    export default {

        mixins: [
            FadeMixin,
            SetDurationMixin
        ],

        props: {
            src: {
                type: Object
            },
            playlist: {
                type: Array
            },
            autoplay: {
                type: Boolean,
                default: false
            },
            playing: {
                type: Boolean,
                default: true
            },
            controls: {
                type: Boolean,
                default: true
            },
            muted: {
                type: Boolean,
                default: false
            },
            loop: {
                type: Boolean,
                default: false
            }
        },

        data() {
            return {
                player: null,
                durationTimer: null,
                emittedDuration: null,
            };
        },

        watch: {
            src() {
                this.updatePlayer();
            },

            playlist() {
                this.updatePlayer();
            },

            playing() {
                if (!this.player) {
                    return;
                }

                console.log('Video playing', this.playing);

                if (this.playing) {
                    this.play();
                } else {
                    this.pause();
                }
            },

            muted() {
                if (!this.player) {
                    return;
                }

                if (this.muted) {
                    this.player.volume(0);
                }
            }
        },

        mounted() {
            this.createPlayer();
        },

        methods: {
            play() {
                const playResponse = this.player.play();

                if (playResponse && (playResponse.catch || playResponse.then)) {
                    playResponse.catch((error) => {
                        console.error('Ignorable error: ', error);
                        this.fadeIn().then(() => {
                            this.player.play();
                        });
                    });

                    playResponse.then(() => {
                        this.fadeIn();
                    });
                } else {
                    this.fadeIn();
                }
            },

            pause() {
                this.fadeOut().then(() => {
                    this.player.pause();
                });
            },

            createPlayer() {
                if (this.src) {
                    this.player = this.createSrcPlayer();
                } else if (this.playlist) {
                    this.player = this.createPlaylistPlayer();
                }

                if (!this.player) {
                    return;
                }

                this.player.on('loadeddata', () => {
                    if (this.autoplay && this.playing) {
                        console.log('Video autoload');

                        this.play();
                    }
                });

                this.player.on('ended', () => {
                    console.log('Video ended');

                    if (this.playlist && this.autoplay) {
                        console.log('Next video autoplay, current: ' + this.player.playlist.currentIndex());

                        if (this.player.playlist.next()) {
                            this.play();
                        }
                    }

                    if (this.isFinished()) {
                        this.$emit('finish');
                    }
                });

                this.emittedDuration = null;

                if (this.durationTimer) {
                    clearInterval(this.durationTimer);
                }

                this.durationTimer = setInterval(() => {
                    if (!this.hasVideos() || this.isFinished()) {
                        return;
                    }

                    const currentDuration = this.getFullDuration();

                    if (this.emittedDuration === currentDuration && this.emittedDuration !== null) {
                        return;
                    }

                    this.emittedDuration = currentDuration;
                    this.$emit('durationUpdated', currentDuration);
                }, 100);

                this.initFadeTimer();

                if (this.autoplay && this.playing) {
                    console.log('Video autoplay');

                    this.play();
                }
            },

            hasVideos() {
                if (this.src) {
                    return true;
                } else if (this.playlist.length) {
                    return true;
                }

                return false;
            },

            getFullDuration() {
                if (this.src) {
                    return this.player.currentTime();
                }

                if (this.playlist.length) {
                    const currentIndex = this.player.playlist.currentItem();
                    const previousDurations = this.getPlaylistItems().reduce((count, playlistItem, index) => {
                        if (index >= currentIndex) {
                            return count;
                        }

                        return count + Number(playlistItem.sources[0].duration);
                    }, 0);

                    return previousDurations + this.player.currentTime();
                }

                return 0;
            },

            isFinished() {
                if (this.playlist && this.playlist.length && this.player.playlist.currentItem() < this.playlist.length - 1) {
                    return false;
                }

                return this.player.ended();
            },

            updatePlayer() {
                if (!this.src) {
                    if (this.player && !this.player.paused() && this.playing) {
                        console.log('Video not paused');
                        this.pause();
                    }

                    if (!this.playlist.length) {
                        return;
                    }
                }

                if (!this.player) {
                    this.createPlayer();
                    return;
                }

                if (this.src) {
                    this.player.src({
                        src: this.src.src,
                        type: 'application/x-mpegURL'
                    });
                } else if (this.playlist) {
                    const playlist = this.getPlaylistItems();

                    this.player.playlist(playlist);
                    this.player.src(playlist[0].sources[0]);
                }

                this.player.currentTime(0);
            },

            getOptions() {
                return {
                    autoplay: this.autoplay && this.playing,
                    loop: this.loop,
                    controls: this.controls,
                    controlBar: this.controls ? {
                        fullscreenToggle: false,
                        pictureInPictureToggle: false,
                    } : false,
                    width: '100%',
                    height: '30px',
                    inactivityTimeout: 0,
                    replay: false,
                    userActions: {
                        hotkeys: false
                    },
                };
            },

            createSrcPlayer() {
                videojs.Vhs.GOAL_BUFFER_LENGTH = goalBufferLength;
                videojs.Vhs.MAX_GOAL_BUFFER_LENGTH = maxGoalBufferLength;

                const options = {
                    ...this.getOptions(),
                    ...{
                        sources: [
                            {
                                src: this.src.src,
                                type: 'application/x-mpegURL'
                            }
                        ]
                    }
                };

                return videojs(this.$refs.videoPlayer, options);
            },

            createPlaylistPlayer() {
                videojs.Vhs.GOAL_BUFFER_LENGTH = goalBufferLength;
                videojs.Vhs.MAX_GOAL_BUFFER_LENGTH = maxGoalBufferLength;

                const player = videojs(this.$refs.videoPlayer, this.getOptions());

                player.playlist(this.getPlaylistItems());

                player.playlist.autoadvance(0);

                return player;
            },

            getPlaylistItems() {
                return this.playlist.map(playlistItem => {
                    return {
                        sources: [
                            {
                                src: playlistItem.src,
                                type: 'application/x-mpegURL',
                                duration: playlistItem.duration
                            }
                        ]
                    };
                });
            },

            getCurrentVolume() {
                if (!this.player) {
                    return 'no-player';
                }

                return Math.round(this.player.volume() * 100) + '%';
            },

            setCurrentTime(time) {
                if (!this.player) {
                    return;
                }

                this.player.currentTime(time);
            },

            pausePlayer() {
                if (!this.player) {
                    return;
                }

                this.player.pause();
            },

            playPlayer() {
                if (!this.player) {
                    return;
                }

                this.player.play();
            }
        },

        beforeDestroy() {
            if (this.player) {
                console.log('Video destroy');
                this.pause();

                setTimeout(() => {
                    this.player.dispose();
                }, this.fadeTime);
            }

            if (this.durationTimer) {
                clearInterval(this.durationTimer);
            }

            this.destroyFadeTimer();
        }
    };
</script>

<style lang="scss">
    .vjs-audio-only-skin {
        width: 100% !important;
        min-width: 200px !important;
        height: 30px !important;
        background: transparent !important;

        .vjs-big-play-button {
            display: none !important;
        }

        .vjs-control-bar {
            display: flex !important;
            border-radius: 30px !important;
            background-color: #e3e3e3 !important;
            color: #000 !important;
            opacity: 1 !important;
        }
    }

    .vjs-loading-spinner {
        display: none!important;
    }

    .videoWrapper {
        opacity: 0;

        &.in {
            opacity: 1;
        }

        &.out {
            opacity: 0;
        }
    }

</style>
